import { CardData, TransactionData } from './types';
export const apiUrl = import.meta.env.VITE_API_URL;
export const wsUrl = import.meta.env.VITE_WS_URL;
export const sseUrl = import.meta.env.VITE_SSE_URL;


export const fetchTotalTransactions = async (): Promise<TransactionData> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ total: 18000 }); 
    }, 1000);
  });
};

export const fetchTotalPerCreditCard = async (): Promise<{ cards: CardData[] }> => {
 
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        cards: [
          { type: 'Mada', total: 10000 },
          { type: 'Visa', total: 5000 },
          { type: 'MasterCard', total: 3000 },
        ],
      });
    }, 1000);
  });
};

// ECR Dashboard API
export interface MerchantListParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: string;
  status?: string;
  searchTerm?: string;
}

export const ecrDashboardApi = {
  getSummary: (token: string) => {
    return fetch(`${apiUrl}/ecr/dashboard/summary`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard summary');
      }
      return response.json();
    });
  },

  getMerchants: (token: string, params: MerchantListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);
    if (params.status) queryParams.append('status', params.status);
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);

    const queryString = queryParams.toString();
    const url = `${apiUrl}/ecr/dashboard/merchants${queryString ? `?${queryString}` : ''}`;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch merchants');
      }
      return response.json();
    });
  },

  getMerchantDetails: (token: string, merchantId: number) => {
    return fetch(`${apiUrl}/ecr/dashboard/merchant/${merchantId}/terminals`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch merchant details');
      }
      return response.json();
    });
  },

  getLicenseTrends: (token: string, months: number = 12) => {
    return fetch(`${apiUrl}/ecr/dashboard/license-trends?months=${months}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch license trends');
      }
      return response.json();
    });
  },

  getLicenseBreakdown: (token: string) => {
    return fetch(`${apiUrl}/ecr/dashboard/license-breakdown`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch license breakdown');
      }
      return response.json();
    });
  },
};

// ECR Subscription Approval API
export interface PendingSubscriptionsParams {
  page?: number;
  size?: number;
}

export const ecrSubscriptionApi = {
  /**
   * Approve a pending subscription request.
   * @param token - JWT authentication token
   * @param subscriptionId - ID of the subscription to approve
   * @returns The approved subscription DTO
   */
  approveSubscription: (token: string, subscriptionId: number) => {
    return fetch(`${apiUrl}/ecr-subscriptions/${subscriptionId}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to approve subscription');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  /**
   * Reject a pending subscription request.
   * @param token - JWT authentication token
   * @param subscriptionId - ID of the subscription to reject
   * @param reason - Optional rejection reason (max 500 characters)
   * @returns The rejected subscription DTO
   */
  rejectSubscription: (token: string, subscriptionId: number, reason?: string) => {
    return fetch(`${apiUrl}/ecr-subscriptions/${subscriptionId}/reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: reason ? JSON.stringify({ reason }) : undefined,
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to reject subscription');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  /**
   * Get pending subscription requests for the authenticated ECR provider.
   * @param token - JWT authentication token
   * @param params - Pagination parameters
   * @returns Paginated list of pending subscriptions
   */
  getPendingSubscriptions: (token: string, params: PendingSubscriptionsParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());

    const queryString = queryParams.toString();
    const url = `${apiUrl}/ecr-subscriptions/pending${queryString ? `?${queryString}` : ''}`;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch pending subscriptions');
      }
      return response.json();
    });
  },

  /**
   * Get subscription counts by status for the authenticated ECR provider.
   * @param token - JWT authentication token
   * @returns Subscription counts DTO with counts for each status
   */
  getSubscriptionCounts: (token: string) => {
    return fetch(`${apiUrl}/ecr-subscriptions/counts`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch subscription counts');
      }
      return response.json();
    });
  },

  /**
   * Get subscription summary for the authenticated ECR provider.
   * Returns total merchants, total terminals, and license information.
   * @param token - JWT authentication token
   * @returns Subscription summary DTO
   */
  getSubscriptionSummary: (token: string) => {
    return fetch(`${apiUrl}/ecr-subscriptions/summary`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch subscription summary');
      }
      return response.json();
    });
  },

  /**
   * Get the ECR subscription status for the authenticated merchant.
   * @param token - JWT authentication token
   * @returns The merchant's ECR subscription DTO, or null if no subscription exists
   */
  getMySubscription: (token: string) => {
    return fetch(`${apiUrl}/ecr-subscriptions/my-subscription`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then((response) => {
      if (response.status === 204) {
        return null; // No subscription exists
      }
      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }
      return response.json();
    });
  },
};

// Provider Mapping API
export interface ProviderMappingListParams {
  page?: number;
  size?: number;
  status?: string;
  searchTerm?: string;
}

export const providerMappingApi = {
  /**
   * Get all providers with pagination and filtering
   * @param token - JWT authentication token
   * @param params - Pagination and filter parameters
   * @returns Paginated list of providers
   */
  getProviders: (token: string, params: ProviderMappingListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);

    const queryString = queryParams.toString();
    const url = `${apiUrl}/provider-mappings/providers${queryString ? `?${queryString}` : ''}`;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch providers');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  /**
   * Get provider details by ID
   * @param token - JWT authentication token
   * @param providerId - ID of the provider
   * @returns Provider details with nested data
   */
  getProviderDetails: (token: string, providerId: number) => {
    return fetch(`${apiUrl}/provider-mappings/providers/${providerId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch provider details');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  /**
   * Toggle provider status (activate/deactivate)
   * @param token - JWT authentication token
   * @param providerId - ID of the provider
   * @returns Updated provider
   */
  toggleProviderStatus: (token: string, providerId: number) => {
    return fetch(`${apiUrl}/provider-mappings/providers/${providerId}/status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to toggle provider status');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  /**
   * Create a new provider
   * @param token - JWT authentication token
   * @param data - Provider creation data
   * @returns Created provider
   */
  createProvider: (token: string, data: { name: string; nameAr: string }) => {
    return fetch(`${apiUrl}/provider-mappings/providers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to create provider');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  /**
   * Update an existing provider
   * @param token - JWT authentication token
   * @param providerId - ID of the provider
   * @param data - Provider update data
   * @returns Updated provider
   */
  updateProvider: (token: string, providerId: number, data: { name: string; nameAr: string; totalLicenses?: number | null }) => {
    return fetch(`${apiUrl}/provider-mappings/providers/${providerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to update provider');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  /**
   * Delete a provider
   * @param token - JWT authentication token
   * @param providerId - ID of the provider
   * @returns Success message
   */
  deleteProvider: (token: string, providerId: number) => {
    return fetch(`${apiUrl}/provider-mappings/providers/${providerId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to delete provider');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  // Package Management
  createPackage: (token: string, data: { name: string; providerId: number; isActive?: boolean; priority?: number; isAmountInSAR?: boolean }) => {
    return fetch(`${apiUrl}/provider-mappings/packages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to create package');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        (error as any).errors = errorData.errors;
        throw error;
      }
      return response.json();
    });
  },

  updatePackage: (token: string, packageId: number, data: { name: string; providerId: number; isActive?: boolean; priority?: number; isAmountInSAR?: boolean }) => {
    return fetch(`${apiUrl}/provider-mappings/packages/${packageId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to update package');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        (error as any).errors = errorData.errors;
        throw error;
      }
      return response.json();
    });
  },

  deletePackage: (token: string, packageId: number) => {
    return fetch(`${apiUrl}/provider-mappings/packages/${packageId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to delete package');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  // Intent Action Management
  createIntentAction: (token: string, data: { actionName: string; actionString: string; responseKey: string; packageId: number }) => {
    return fetch(`${apiUrl}/provider-mappings/intent-actions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to create intent action');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  updateIntentAction: (token: string, intentActionId: number, data: { actionName: string; actionString: string; responseKey: string; packageId: number }) => {
    return fetch(`${apiUrl}/provider-mappings/intent-actions/${intentActionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to update intent action');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  deleteIntentAction: (token: string, intentActionId: number) => {
    return fetch(`${apiUrl}/provider-mappings/intent-actions/${intentActionId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to delete intent action');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  // Action Parameter Management
  createActionParam: (token: string, data: { name: string; value: string; dataType: string; intentActionId: number; stdIntentActionParamId: number }) => {
    return fetch(`${apiUrl}/provider-mappings/action-params`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to create action parameter');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  updateActionParam: (token: string, paramId: number, data: { name: string; value: string; dataType: string; intentActionId: number; stdIntentActionParamId: number }) => {
    return fetch(`${apiUrl}/provider-mappings/action-params/${paramId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to update action parameter');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  deleteActionParam: (token: string, paramId: number) => {
    return fetch(`${apiUrl}/provider-mappings/action-params/${paramId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to delete action parameter');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  // Field Mapping Management
  createFieldMapping: (token: string, data: { providerFieldName: string; providerId: number; stdFieldId: number; type: number }) => {
    return fetch(`${apiUrl}/provider-mappings/field-mappings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to create field mapping');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  updateFieldMapping: (token: string, mappingId: number, data: { providerFieldName: string; providerId: number; stdFieldId: number; type: number }) => {
    return fetch(`${apiUrl}/provider-mappings/field-mappings/${mappingId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to update field mapping');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  deleteFieldMapping: (token: string, mappingId: number) => {
    return fetch(`${apiUrl}/provider-mappings/field-mappings/${mappingId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to delete field mapping');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  // Reference Data
  getStdFields: (token: string) => {
    return fetch(`${apiUrl}/provider-mappings/std-fields`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch standard fields');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  getStdIntentParams: (token: string) => {
    return fetch(`${apiUrl}/provider-mappings/std-intent-params`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch standard intent parameters');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  // Impact Assessment APIs
  getProviderImpact: (token: string, providerId: number) => {
    return fetch(`${apiUrl}/provider-mappings/providers/${providerId}/impact`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch provider impact');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  getPackageImpact: (token: string, packageId: number) => {
    return fetch(`${apiUrl}/provider-mappings/packages/${packageId}/impact`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch package impact');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  getIntentActionImpact: (token: string, intentActionId: number) => {
    return fetch(`${apiUrl}/provider-mappings/intent-actions/${intentActionId}/impact`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch intent action impact');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  getFieldMappingImpact: (token: string, fieldMappingId: number) => {
    return fetch(`${apiUrl}/provider-mappings/field-mappings/${fieldMappingId}/impact`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch field mapping impact');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  // Terminal Model Associations
  getProviderTerminalModels: (token: string, providerId: number) => {
    return fetch(`${apiUrl}/provider-mappings/providers/${providerId}/terminal-models`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch terminal models');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  updateProviderTerminalModels: (token: string, providerId: number, terminalModelIds: number[]) => {
    return fetch(`${apiUrl}/provider-mappings/providers/${providerId}/terminal-models`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(terminalModelIds),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to update terminal models');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },
};

// MSP Dashboard API
export interface MspMerchantListParams {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
}

export interface MspTerminalListParams {
  page?: number;
  size?: number;
  search?: string;
  sort?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface MspTerminalDirectoryParams {
  page?: number;
  size?: number;
  search?: string;
  merchantId?: number;
  status?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

// Module 5 — Billing Integration types
export interface MspBillingEventParams {
  page?: number;
  size?: number;
  type?: string;
  billable?: boolean;
  syncStatus?: string;
  merchantId?: number;
  search?: string;
  from?: string;
  to?: string;
}

export interface BillingSummary {
  providerId: number;
  providerName: string;
  totalLicenses: number;
  usedLicenses: number;
  availableLicenses: number;
  contractType: string;
  monthlyLicenseRate: number | null;
  shieldThresholdPercent: number;
  shieldWarning: boolean;
  shieldBlocked: boolean;
}

export interface BillingEventRow {
  id: number;
  eventType: string;
  providerId?: number | null;
  providerName?: string | null;
  merchantId: number | null;
  merchantName: string | null;
  terminalId: number | null;
  madaId: string | null;
  billable: boolean;
  contractTypeAtEvent: string | null;
  timestamp: string;
  syncStatus: 'PENDING' | 'SENT' | 'FAILED';
  sentAt: string | null;
}

export interface BillingUsageSummary {
  id: number;
  providerId: number;
  providerName: string | null;
  periodYear: number;
  periodMonth: number;
  snapshotDate: string;
  generatedAt: string;
  trigger: 'SCHEDULED' | 'MANUAL';
  totalBillableUnits: number;
  monthlyRateAtGeneration: number | null;
  contractTypeAtGeneration: string | null;
}

export interface AdminBillingEventParams extends MspBillingEventParams {
  providerId?: number;
}

export interface BillingOutboxStats {
  pending: number;
  sent: number;
  failed: number;
  deadLettered: number;
}

export interface BillingTerminalRow {
  id: number;
  madaId: string | null;
  merchantId: number | null;
  merchantName: string | null;
  activatedAt: string | null;
  mspSuspended: boolean;
  approvalStatus?: string | null;
  merchantOwnership?: string | null;
  providerId?: number | null;
  providerName?: string | null;
  packageId?: number | null;
  packageName?: string | null;
}

export interface BillingPackageRow {
  id: number;
  name: string;
  priority: number;
}

export interface MspOrganizationUpdatePayload {
  name: string;
  nameAr?: string;
  status: string;
  totalLicenses: number;
  licenseActivationDate?: string;
  licenseExpiryDate?: string;
  contractType?: string;
  monthlyLicenseRate?: number;
  inactivityThresholdDays?: number;
}

export const mspApi = {
  /**
   * Get the MSP dashboard summary for the authenticated MSP user.
   * Returns license utilization metrics, active/suspended terminal counts,
   * expiry information, and provider details.
   * @param token - JWT authentication token
   * @returns MspDashboardDto
   */
  getDashboard: (token: string) => {
    return fetch(`${apiUrl}/msp/dashboard`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch MSP dashboard');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  /**
   * Get the paginated merchant directory for the authenticated MSP user.
   * Only merchants with terminals scoped to this MSP's provider are returned.
   * @param token - JWT authentication token
   * @param params - Pagination, search, and status filter parameters
   * @returns Paginated Page<MspMerchantListDto>
   */
  getMerchants: (token: string, params: MspMerchantListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = `${apiUrl}/msp/merchants${queryString ? `?${queryString}` : ''}`;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch MSP merchants');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  /**
   * Get the paginated, searchable terminal list for a specific merchant,
   * scoped to the authenticated MSP user's provider.
   * Only terminals whose madaPackage.provider matches this user's provider are returned.
   * @param token      - JWT authentication token
   * @param merchantId - The merchant ID
   * @param params     - Pagination, search, and sort parameters
   * @returns Paginated Page<MspTerminalDto>
   */
  getMerchantTerminals: (token: string, merchantId: number, params: MspTerminalListParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.sort) queryParams.append('sort', params.sort);

    const queryString = queryParams.toString();
    const url = `${apiUrl}/msp/merchants/${merchantId}/terminals${queryString ? `?${queryString}` : ''}`;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch merchant terminals');
        (error as any).code = errorData.code;
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  getLicenseTrends: (token: string, months = 12) => {
    return fetch(`${apiUrl}/msp/dashboard/license-trends?months=${months}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch license trends');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  getLicenseBreakdown: (token: string) => {
    return fetch(`${apiUrl}/msp/dashboard/license-breakdown`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch license breakdown');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  getRecentActivity: (token: string, limit = 8) => {
    return fetch(`${apiUrl}/msp/dashboard/recent-activity?limit=${limit}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch recent activity');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  getTerminals: (token: string, params: MspTerminalDirectoryParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.merchantId !== undefined) queryParams.append('merchantId', params.merchantId.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDir) queryParams.append('sortDir', params.sortDir);

    const queryString = queryParams.toString();
    const url = `${apiUrl}/msp/terminals${queryString ? `?${queryString}` : ''}`;

    return fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch terminals');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  suspendTerminal: (token: string, terminalId: number) =>
    fetch(`${apiUrl}/msp/terminals/${terminalId}/suspend`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to suspend terminal');
      }
      return response.json() as Promise<{ status: string }>;
    }),

  reactivateTerminal: (token: string, terminalId: number) =>
    fetch(`${apiUrl}/msp/terminals/${terminalId}/reactivate`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to reactivate terminal');
        (error as any).status = response.status;
        (error as any).error = errorData.error;
        throw error;
      }
      return response.json() as Promise<{ status: string }>;
    }),

  getTerminalDetails: (token: string, terminalId: number) =>
    fetch(`${apiUrl}/msp/terminals/${terminalId}/details`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch terminal details');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    }),

  getSuspensionLog: (token: string, terminalId: number, page = 0, size = 10) => {
    const url = `${apiUrl}/msp/terminals/${terminalId}/suspension-log?page=${page}&size=${size}`;
    return fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch suspension log');
      }
      return response.json();
    });
  },

  updateInactivityThreshold: (token: string, inactivityThresholdDays: number | null) =>
    fetch(`${apiUrl}/msp/settings/inactivity-threshold`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify({ inactivityThresholdDays }),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update inactivity threshold');
      }
      return response.json() as Promise<{ inactivityThresholdDays: number }>;
    }),

  getTerminalReportPaged: (
    token: string,
    params: {
      period: string;
      from?: string;
      to?: string;
      search?: string;
      page?: number;
      size?: number;
      sortBy?: string;
      sortDir?: string;
    },
  ) => {
    const q = new URLSearchParams({ period: params.period });
    if (params.from)    q.append('from',    params.from);
    if (params.to)      q.append('to',      params.to);
    if (params.search)  q.append('search',  params.search);
    if (params.page  !== undefined) q.append('page',    String(params.page));
    if (params.size  !== undefined) q.append('size',    String(params.size));
    if (params.sortBy)  q.append('sortBy',  params.sortBy);
    if (params.sortDir) q.append('sortDir', params.sortDir);
    return fetch(`${apiUrl}/msp/transaction-report/terminals?${q}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch terminal report');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  getTransactionReport: (
    token: string,
    period: string,
    from?: string,
    to?: string,
  ) => {
    const params = new URLSearchParams({ period });
    if (period === 'custom' && from && to) {
      params.append('from', from);
      params.append('to', to);
    }
    return fetch(`${apiUrl}/msp/transaction-report?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch transaction report');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  /**
   * Download the MSP transaction report (by-merchant or by-terminal).
   * Returns the raw Response so the caller can handle both binary blobs
   * (direct download) and JSON payloads (async email flow, >2000 tx).
   *
   * statuses — optional list of status codes (0-9). When omitted or empty,
   * all status columns are included in the exported file.
   */
  downloadTransactionReport: (
    token: string,
    params: {
      scope: 'merchant' | 'terminal';
      format: 'csv' | 'xlsx';
      period: string;
      from?: string;
      to?: string;
      statuses?: number[];
    },
  ) => {
    const q = new URLSearchParams({
      scope: params.scope,
      format: params.format,
      period: params.period,
    });
    if (params.from) q.append('from', params.from);
    if (params.to)   q.append('to',   params.to);
    if (params.statuses && params.statuses.length > 0) {
      for (const s of params.statuses) q.append('statuses', String(s));
    }
    return fetch(`${apiUrl}/msp/transaction-report/download?${q.toString()}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
  },

  // ---------------------------------------------------------------------------
  // Module 5 — Billing Integration
  // ---------------------------------------------------------------------------

  /** FR-5.1 license summary + Billing Shield state for the authenticated MSP. */
  getBillingSummary: (token: string) => {
    return fetch(`${apiUrl}/msp/billing/summary`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch billing summary');
        (error as any).status = response.status;
        throw error;
      }
      return response.json() as Promise<BillingSummary>;
    });
  },

  getBillingEvents: (token: string, params: MspBillingEventParams = {}) => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append('page', String(params.page));
    if (params.size !== undefined) q.append('size', String(params.size));
    if (params.type) q.append('type', params.type);
    if (params.billable !== undefined) q.append('billable', String(params.billable));
    if (params.syncStatus) q.append('syncStatus', params.syncStatus);
    if (params.merchantId !== undefined) q.append('merchantId', String(params.merchantId));
    if (params.search) q.append('search', params.search);
    if (params.from) q.append('from', params.from);
    if (params.to) q.append('to', params.to);
    const queryString = q.toString();
    return fetch(`${apiUrl}/msp/billing/events${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch billing events');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  getUsageSummaries: (token: string) => {
    return fetch(`${apiUrl}/msp/billing/usage-summaries`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch usage summaries');
        (error as any).status = response.status;
        throw error;
      }
      return response.json() as Promise<BillingUsageSummary[]>;
    });
  },

  generateUsageSummary: (token: string, year: number, month: number) => {
    return fetch(`${apiUrl}/msp/billing/usage-summaries/generate?year=${year}&month=${month}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to generate usage summary');
        (error as any).status = response.status;
        throw error;
      }
      return response.json() as Promise<BillingUsageSummary>;
    });
  },

  /** Downloads a stored monthly usage summary as CSV (type: 'merchant' | 'terminal'). */
  downloadUsageCsv: (token: string, summaryId: number, type: 'merchant' | 'terminal') => {
    return fetch(`${apiUrl}/msp/billing/usage-summaries/${summaryId}/csv?type=${type}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to download usage CSV');
        (error as any).status = response.status;
        throw error;
      }
      return response.text();
    });
  },
};

// MSP — Terminal Approval workflow (User Story: MSP-Merchant Ownership)
export const mspApprovalApi = {
  listPending: (
    token: string,
    params: { page?: number; size?: number; search?: string; status?: string; sortBy?: string; sortDir?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append('page', String(params.page));
    if (params.size !== undefined) q.append('size', String(params.size));
    if (params.search) q.append('search', params.search);
    if (params.status) q.append('status', params.status);
    if (params.sortBy) q.append('sortBy', params.sortBy);
    if (params.sortDir) q.append('sortDir', params.sortDir);
    const qs = q.toString();
    return fetch(`${apiUrl}/msp/terminals/pending-approvals${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load pending approvals');
      }
      return r.json();
    });
  },

  count: (token: string) =>
    fetch(`${apiUrl}/msp/terminals/pending-approvals/count`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) return { pending: 0 };
      return r.json() as Promise<{ pending: number }>;
    }),

  listConflicts: (
    token: string,
    params: { page?: number; size?: number; search?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append('page', String(params.page));
    if (params.size !== undefined) q.append('size', String(params.size));
    if (params.search) q.append('search', params.search);
    const qs = q.toString();
    return fetch(`${apiUrl}/msp/terminals/conflicts${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load conflicts');
      }
      return r.json();
    });
  },

  countConflicts: (token: string) =>
    fetch(`${apiUrl}/msp/terminals/conflicts/count`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) return { conflicts: 0 };
      return r.json() as Promise<{ conflicts: number }>;
    }),

  approve: (token: string, terminalId: number) =>
    fetch(`${apiUrl}/msp/terminals/${terminalId}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const e = new Error(err.message || 'Failed to approve terminal');
        (e as any).status = r.status;
        (e as any).body = err;
        throw e;
      }
      return r.json();
    }),

  reject: (token: string, terminalId: number, reason?: string) =>
    fetch(`${apiUrl}/msp/terminals/${terminalId}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify({ reason: reason ?? null }),
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to reject terminal');
      }
      return r.json();
    }),

  bulkApprove: (token: string, terminalIds: number[]) =>
    fetch(`${apiUrl}/msp/terminals/bulk-approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify({ terminalIds }),
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const e = new Error(err.message || 'Failed to bulk approve');
        (e as any).status = r.status;
        (e as any).body = err;
        throw e;
      }
      return r.json() as Promise<{ approvedCount: number; skipped: Array<{ terminalId: number; reason: string }> }>;
    }),

  bulkReject: (token: string, terminalIds: number[], reason?: string) =>
    fetch(`${apiUrl}/msp/terminals/bulk-reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify({ terminalIds, reason: reason ?? null }),
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to bulk reject');
      }
      return r.json() as Promise<{ rejectedCount: number; skipped: Array<{ terminalId: number; reason: string }> }>;
    }),
};

// Merchant — self-serve endpoints for the ownership-aware UI
export const merchantSelfApi = {
  getOwnership: (token: string) =>
    fetch(`${apiUrl}/merchants/me/ownership`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) return { merchantOwnership: null as string | null };
      return r.json() as Promise<{ merchantOwnership: string | null }>;
    }),

  resubmitTerminal: (
    token: string,
    terminalId: number,
    body?: { madaId?: string; providerId?: number; modelId?: number; packageId?: number },
  ) =>
    fetch(`${apiUrl}/terminals/${terminalId}/resubmit`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify(body ?? {}),
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to resubmit terminal');
      }
      return r.json().catch(() => ({}));
    }),
};

export interface MspAllowedPackage {
  packageId: number;
  packageName: string;
  originated: boolean;
  originatingProviderName: string | null;
  approvedTerminalCount: number;
}

export interface MspAvailablePackage {
  packageId: number;
  packageName: string;
  originatingProviderName: string | null;
}

export interface MspPrefix {
  id: number;
  name: string;
}

export const mspSettingsApi = {
  listPackages: (token: string): Promise<MspAllowedPackage[]> =>
    fetch(`${apiUrl}/msp/settings/packages`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load allowed packages');
      }
      return r.json();
    }),

  listAvailablePackages: (token: string, search?: string): Promise<MspAvailablePackage[]> => {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    return fetch(`${apiUrl}/msp/settings/packages/available${q}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load available packages');
      }
      return r.json();
    });
  },

  addPackage: (token: string, packageId: number): Promise<MspAllowedPackage> =>
    fetch(`${apiUrl}/msp/settings/packages/${packageId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to add package');
      }
      return r.json();
    }),

  removePackage: (token: string, packageId: number): Promise<void> =>
    fetch(`${apiUrl}/msp/settings/packages/${packageId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const e = new Error(err.message || 'Failed to remove package');
        (e as any).status = r.status;
        throw e;
      }
    }),

  listPrefixes: (token: string): Promise<MspPrefix[]> =>
    fetch(`${apiUrl}/msp/settings/prefixes`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load prefixes');
      }
      return r.json();
    }),

  claimPrefix: (token: string, name: string): Promise<MspPrefix> =>
    fetch(`${apiUrl}/msp/settings/prefixes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify({ name }),
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const e = new Error(err.message || 'Failed to claim prefix');
        (e as any).status = r.status;
        throw e;
      }
      return r.json();
    }),

  releasePrefix: (token: string, prefixId: number): Promise<void> =>
    fetch(`${apiUrl}/msp/settings/prefixes/${prefixId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const e = new Error(err.message || 'Failed to release prefix');
        (e as any).status = r.status;
        throw e;
      }
    }),
};

export interface UnknownPackageTerminal {
  terminalId: number;
  madaId: string;
  merchantId: number | null;
  merchantName: string | null;
  detectedPackageId: number | null;
  detectedPackageName: string | null;
  suggestedProviderId: number | null;
  suggestedProviderName: string | null;
  submittedAt: string | null;
}

export interface AdminRejectedTerminal {
  terminalId: number;
  madaId: string;
  merchantId: number | null;
  merchantName: string | null;
  rejectedByProviderId: number | null;
  rejectedByProviderName: string | null;
  detectedPackageName: string | null;
  rejectionReason: string | null;
  rejectedAt: string | null;
  ownershipFlag: string | null;
}

export interface AdminTerminalPreview {
  terminalId: number;
  madaId: string;
  approvalStatus: string;
  merchantId: number | null;
  merchantName: string | null;
  currentOwnerProviderId: number | null;
  currentOwnerProviderName: string | null;
  detectedPackageId: number | null;
  detectedPackageName: string | null;
  detectedPackageOriginatorName: string | null;
  ownershipFlag: string | null;
  activatedAt: string | null;
}

export const adminOwnershipApi = {
  listUnknownPackageTerminals: (
    token: string,
    params: { page?: number; size?: number; search?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append('page', String(params.page));
    if (params.size !== undefined) q.append('size', String(params.size));
    if (params.search) q.append('search', params.search);
    const qs = q.toString();
    return fetch(`${apiUrl}/admin/msp-organizations/unknown-package-terminals${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load unknown-package terminals');
      }
      return r.json() as Promise<{
        content: UnknownPackageTerminal[];
        totalElements: number;
        totalPages: number;
        number: number;
        size: number;
      }>;
    });
  },

  assignPackageToMsp: (token: string, providerId: number, packageId: number) =>
    fetch(`${apiUrl}/admin/msp-organizations/${providerId}/allowed-packages/${packageId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to assign package');
      }
      return r.json() as Promise<{ promotedTerminalCount: number; providerId: number; packageId: number }>;
    }),

  transferOwnership: (token: string, terminalId: number, newProviderId: number) =>
    fetch(`${apiUrl}/admin/msp-organizations/terminals/${terminalId}/transfer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify({ newProviderId }),
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to transfer ownership');
      }
      return r.json() as Promise<{ terminalId: number; newProviderId: number }>;
    }),

  listRejectedTerminals: (
    token: string,
    params: { page?: number; size?: number; search?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append('page', String(params.page));
    if (params.size !== undefined) q.append('size', String(params.size));
    if (params.search) q.append('search', params.search);
    const qs = q.toString();
    return fetch(`${apiUrl}/admin/msp-organizations/rejected-terminals${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to load rejected terminals');
      }
      return r.json() as Promise<{
        content: AdminRejectedTerminal[];
        totalElements: number;
        totalPages: number;
        number: number;
        size: number;
      }>;
    });
  },

  lookupTerminalByMadaId: (token: string, madaId: string): Promise<AdminTerminalPreview> =>
    fetch(`${apiUrl}/admin/msp-organizations/terminals/lookup?madaId=${encodeURIComponent(madaId)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const msg = err.message || err.error || `Failed to lookup terminal (HTTP ${r.status})`;
        const e = new Error(msg);
        (e as any).status = r.status;
        throw e;
      }
      return r.json();
    }),

  rejectUnknownPackageTerminal: (token: string, terminalId: number, reason?: string) =>
    fetch(`${apiUrl}/admin/msp-organizations/terminals/${terminalId}/reject-unknown`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify({ reason: reason ?? null }),
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to reject terminal');
      }
      return r.json();
    }),
};

// Admin — force approve/reject stuck pending terminals
export const adminApprovalApi = {
  forceApprove: (token: string, terminalId: number) =>
    fetch(`${apiUrl}/admin/msp-organizations/terminals/${terminalId}/force-approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to force approve');
      }
      return r.json();
    }),

  forceReject: (token: string, terminalId: number, reason?: string) =>
    fetch(`${apiUrl}/admin/msp-organizations/terminals/${terminalId}/force-reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify({ reason: reason ?? null }),
    }).then(async (r) => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to force reject');
      }
      return r.json();
    }),
};

// Admin — MSP organization management (Module 5 contract configuration)
export const adminMspApi = {
  listMspOrganizations: (token: string, page = 0, size = 20) => {
    return fetch(`${apiUrl}/admin/msp-organizations?page=${page}&size=${size}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to fetch MSP organizations');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },

  updateMspOrganization: (token: string, id: number, payload: MspOrganizationUpdatePayload) => {
    return fetch(`${apiUrl}/admin/msp-organizations/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      credentials: 'include',
      body: JSON.stringify(payload),
    }).then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Failed to update MSP organization');
        (error as any).status = response.status;
        throw error;
      }
      return response.json();
    });
  },
};

// Admin — Billing test console (Module 5)
const adminBillingCheck = async (response: Response, fallback: string) => {
  if (!response.ok) {
    const errorData: any = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || errorData.error || fallback);
    (error as any).status = response.status;
    throw error;
  }
  return response;
};

const adminBillingHeaders = (token: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

export const adminBillingApi = {
  /** License pool + shield state for every MSP provider. */
  getProviders: (token: string) => {
    return fetch(`${apiUrl}/admin/billing/providers`, {
      method: 'GET', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to fetch MSP billing providers'))
      .then((r) => r.json() as Promise<BillingSummary[]>);
  },

  getEvents: (token: string, params: AdminBillingEventParams = {}) => {
    const q = new URLSearchParams();
    if (params.page !== undefined) q.append('page', String(params.page));
    if (params.size !== undefined) q.append('size', String(params.size));
    if (params.providerId !== undefined) q.append('providerId', String(params.providerId));
    if (params.type) q.append('type', params.type);
    if (params.billable !== undefined) q.append('billable', String(params.billable));
    if (params.syncStatus) q.append('syncStatus', params.syncStatus);
    if (params.merchantId !== undefined) q.append('merchantId', String(params.merchantId));
    if (params.search) q.append('search', params.search);
    if (params.from) q.append('from', params.from);
    if (params.to) q.append('to', params.to);
    const queryString = q.toString();
    return fetch(`${apiUrl}/admin/billing/events${queryString ? `?${queryString}` : ''}`, {
      method: 'GET', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to fetch billing events'))
      .then((r) => r.json());
  },

  /** Re-queues a FAILED / dead-lettered event for delivery. */
  retryEvent: (token: string, eventId: number) => {
    return fetch(`${apiUrl}/admin/billing/events/${eventId}/retry`, {
      method: 'POST', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to retry billing event'))
      .then((r) => r.json() as Promise<BillingEventRow>);
  },

  getOutboxStats: (token: string) => {
    return fetch(`${apiUrl}/admin/billing/outbox/stats`, {
      method: 'GET', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to fetch outbox stats'))
      .then((r) => r.json() as Promise<BillingOutboxStats>);
  },

  /** Runs one outbox batch immediately instead of waiting for the 30s schedule. */
  dispatchOutbox: (token: string) => {
    return fetch(`${apiUrl}/admin/billing/outbox/dispatch`, {
      method: 'POST', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to dispatch outbox'))
      .then((r) => r.json() as Promise<BillingOutboxStats>);
  },

  getTerminals: (token: string, providerId: number, search?: string, limit = 50) => {
    const q = new URLSearchParams({ providerId: String(providerId), limit: String(limit) });
    if (search) q.append('search', search);
    return fetch(`${apiUrl}/admin/billing/terminals?${q.toString()}`, {
      method: 'GET', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to fetch terminals'))
      .then((r) => r.json() as Promise<BillingTerminalRow[]>);
  },

  getUsageSummaries: (token: string, providerId?: number) => {
    const query = providerId !== undefined ? `?providerId=${providerId}` : '';
    return fetch(`${apiUrl}/admin/billing/usage-summaries${query}`, {
      method: 'GET', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to fetch usage summaries'))
      .then((r) => r.json() as Promise<BillingUsageSummary[]>);
  },

  generateUsageSummary: (token: string, providerId: number, year: number, month: number) => {
    return fetch(`${apiUrl}/admin/billing/usage-summaries/generate?providerId=${providerId}&year=${year}&month=${month}`, {
      method: 'POST', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to generate usage summary'))
      .then((r) => r.json() as Promise<BillingUsageSummary>);
  },

  /** Simulates the scheduled 28th-of-month run across all MSPs for any period. */
  generateAllUsageSummaries: (token: string, year: number, month: number) => {
    return fetch(`${apiUrl}/admin/billing/usage-summaries/generate-all?year=${year}&month=${month}`, {
      method: 'POST', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to generate usage summaries'))
      .then((r) => r.json() as Promise<BillingUsageSummary[]>);
  },

  downloadUsageCsv: (token: string, summaryId: number, type: 'merchant' | 'terminal') => {
    return fetch(`${apiUrl}/admin/billing/usage-summaries/${summaryId}/csv?type=${type}`, {
      method: 'GET', headers: { Authorization: `Bearer ${token}` }, credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to download usage CSV'))
      .then((r) => r.text());
  },

  /** Lifecycle simulator — mirrors the production activation/suspend/reactivate paths. */
  simulateActivate: (token: string, terminalId: number) => {
    return fetch(`${apiUrl}/admin/billing/simulate/activate?terminalId=${terminalId}`, {
      method: 'POST', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to simulate activation'))
      .then((r) => r.json() as Promise<BillingSummary>);
  },

  simulateSuspend: (token: string, terminalId: number) => {
    return fetch(`${apiUrl}/admin/billing/simulate/suspend?terminalId=${terminalId}`, {
      method: 'POST', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to simulate suspension'))
      .then((r) => r.json() as Promise<BillingSummary>);
  },

  simulateReactivate: (token: string, terminalId: number) => {
    return fetch(`${apiUrl}/admin/billing/simulate/reactivate?terminalId=${terminalId}`, {
      method: 'POST', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to simulate reactivation'))
      .then((r) => r.json() as Promise<BillingSummary>);
  },

  /** Terminals awaiting first login (DRAFT / WAITING_LOGIN) — no package yet. */
  getTerminalsAwaitingLogin: (token: string, search?: string, limit = 50) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (search) q.append('search', search);
    return fetch(`${apiUrl}/admin/billing/terminals/awaiting-login?${q.toString()}`, {
      method: 'GET', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to fetch terminals awaiting login'))
      .then((r) => r.json() as Promise<BillingTerminalRow[]>);
  },

  /** Active packages for a provider (login simulator picker). */
  getPackagesForProvider: (token: string, providerId: number) => {
    return fetch(`${apiUrl}/admin/billing/packages?providerId=${providerId}`, {
      method: 'GET', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to fetch packages'))
      .then((r) => r.json() as Promise<BillingPackageRow[]>);
  },

  /** Simulates the terminal's first login — assigns package + transitions WAITING_LOGIN → PENDING. */
  simulateLogin: (token: string, terminalId: number, packageId: number) => {
    const q = new URLSearchParams({ terminalId: String(terminalId), packageId: String(packageId) });
    return fetch(`${apiUrl}/admin/billing/simulate/login?${q.toString()}`, {
      method: 'POST', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to simulate login'))
      .then((r) => r.json() as Promise<BillingTerminalRow>);
  },

  /**
   * Day-proration preview — runs the algorithm on a hypothetical terminal timeline
   * and returns the computed activeDays / fraction / billable dates. Nothing is persisted.
   */
  previewProration: (token: string, request: ProrationPreviewRequest) => {
    return fetch(`${apiUrl}/admin/billing/simulate/proration-preview`, {
      method: 'POST',
      headers: adminBillingHeaders(token),
      credentials: 'include',
      body: JSON.stringify(request),
    }).then((r) => adminBillingCheck(r, 'Failed to run proration preview'))
      .then((r) => r.json() as Promise<ProrationPreviewResponse>);
  },
};

export interface ProrationPreviewSuspension {
  suspendedAt: string;
  reactivatedAt?: string | null;
}

export interface ProrationPreviewRequest {
  year: number;
  month: number;
  contractType: 'TYPE_A' | 'TYPE_B';
  activatedAt?: string | null;
  deletedAt?: string | null;
  suspensions?: ProrationPreviewSuspension[];
}

export interface ProrationPreviewInterval {
  start: string;
  end: string;
}

export interface ProrationPreviewResponse {
  daysInPeriod: number;
  activeDays: number;
  fraction: number;
  finalState: string | null;
  excluded: boolean;
  excludedReason: string | null;
  billableIntervals: ProrationPreviewInterval[];
  billableDates: string[];
}

// Audit trail (admin cross-MSP + MSP self-scoped)
export interface AuditLogRow {
  id: number;
  userId: number;
  username: string | null;
  userRole: string | null;
  providerId: number | null;
  providerName: string | null;
  actionType: string;
  ipAddress: string | null;
  timestamp: string;
  details: string | null;
}

export interface AuditLogParams {
  page?: number;
  size?: number;
  providerId?: number;
  userId?: number;
  actionType?: string;
  search?: string;
  from?: string;
  to?: string;
}

const auditQuery = (params: AuditLogParams) => {
  const q = new URLSearchParams();
  if (params.page !== undefined) q.append('page', String(params.page));
  if (params.size !== undefined) q.append('size', String(params.size));
  if (params.providerId !== undefined) q.append('providerId', String(params.providerId));
  if (params.userId !== undefined) q.append('userId', String(params.userId));
  if (params.actionType) q.append('actionType', params.actionType);
  if (params.search) q.append('search', params.search);
  if (params.from) q.append('from', params.from);
  if (params.to) q.append('to', params.to);
  const s = q.toString();
  return s ? `?${s}` : '';
};

export const auditApi = {
  getAdminLogs: (token: string, params: AuditLogParams = {}) => {
    return fetch(`${apiUrl}/admin/audit-logs${auditQuery(params)}`, {
      method: 'GET', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to fetch audit logs'))
      .then((r) => r.json());
  },

  getMspLogs: (token: string, params: AuditLogParams = {}) => {
    return fetch(`${apiUrl}/msp/audit-logs${auditQuery(params)}`, {
      method: 'GET', headers: adminBillingHeaders(token), credentials: 'include',
    }).then((r) => adminBillingCheck(r, 'Failed to fetch audit logs'))
      .then((r) => r.json());
  },
};
